import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { IconChevronDown, IconChevronUp, IconCopy } from "@tabler/icons-react";
import { MToggle } from "@/components/ui/atoms/MToggle";
import MBadge from "@/components/ui/atoms/MBadge";
import MLoader from "@/components/ui/atoms/MLoader";
import movementsService from "@/services/movements.service";
import { familyService } from "@/services/family.service";
import { MovementDebugRow, DebugMovementsResponse } from "@/types/movement";

const CATEGORY_OPTIONS = [
  { key: "all", label: "All Categories" },
  { key: "lab", label: "Lab" },
  { key: "medication", label: "Medication" },
  { key: "encounter", label: "Encounter" },
  { key: "condition", label: "Condition" },
  { key: "health_memory", label: "Health Memory" },
  { key: "record_meta", label: "Record Meta" },
];

function formatEvidenceDate(evidence: Record<string, unknown>): string | null {
  const raw = evidence.source_date;
  if (!raw) return null;
  try {
    return new Date(raw as string).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

interface MemberOption {
  id: string;
  label: string;
}

export const DebugMovementsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlMemberId = searchParams.get("memberId");

  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(
    urlMemberId
  );

  const [activeTab, setActiveTab] = useState<string>("all");
  const [showSuperseded, setShowSuperseded] = useState(true);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [data, setData] = useState<DebugMovementsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    familyService
      .getFamilyMembers({ pageSize: 50 })
      .then((res) => {
        const options: MemberOption[] = res.data.map((fm: any) => ({
          id: fm.member_id ?? fm.id ?? fm._id,
          label:
            `${fm.first_name ?? ""} ${fm.last_name ?? ""}`.trim() ||
            fm.member_id ||
            fm.id ||
            fm._id,
        }));
        setMemberOptions(options);
        if (!selectedMemberId && options.length > 0) {
          setSelectedMemberId(options[0].id);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedMemberId) return;
    setLoading(true);
    setError(null);
    movementsService
      .getDebugMovements(selectedMemberId, {
        page,
        limit: 50,
        category: activeTab === "all" ? undefined : activeTab,
        showSuperseded,
      })
      .then(setData)
      .catch(() => setError(t("debug.movements.errorHeading")))
      .finally(() => setLoading(false));
  }, [selectedMemberId, activeTab, showSuperseded, page, t]);

  const handleMemberChange = (id: string) => {
    setSelectedMemberId(id);
    setPage(1);
    setActiveTab("all");
    setExpandedId(null);
    setData(null);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedId(null);
  };

  const handleRowClick = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
    setCopied(false);
  };

  const handleCopy = async (evidence: Record<string, unknown>) => {
    await navigator.clipboard.writeText(JSON.stringify(evidence, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky header bar */}
      <div className="sticky top-0 bg-white border-b border-neutral-200 px-8 py-4 flex justify-between items-center z-10">
        <h1 className="text-[20px] font-semibold">
          {t("debug.movements.title")}
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-[14px] text-neutral-600">
            {t("debug.movements.showSuperseded")}
          </span>
          <MToggle
            checked={showSuperseded}
            onChange={setShowSuperseded}
            ariaLabel={t("debug.movements.showSuperseded")}
          />
        </div>
      </div>

      {/* Filters bar */}
      <div className="px-4 pt-3 pb-2 border-b border-neutral-200 flex gap-3">
        {memberOptions.length > 0 && (
          <select
            value={selectedMemberId ?? ""}
            onChange={(e) => handleMemberChange(e.target.value)}
            className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {memberOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
        <select
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Content area */}
      <div className="p-4">
        {loading && <MLoader />}

        {!loading && error && (
          <div className="text-center py-12">
            <p className="text-[16px] font-semibold">
              {t("debug.movements.errorHeading")}
            </p>
            <p className="text-[14px] text-neutral-500 mt-1">
              {t("debug.movements.errorBody")}
            </p>
          </div>
        )}

        {!loading && !error && data && data.data.length === 0 && (
          <div className="text-center py-12">
            <p className="text-[16px] font-semibold">
              {t("debug.movements.emptyHeading")}
            </p>
            <p className="text-[14px] text-neutral-500 mt-1">
              {t("debug.movements.emptyBody")}
            </p>
          </div>
        )}

        {!loading && !error && data && data.data.length > 0 && (
          <div className="flex flex-col gap-1">
            {data.data.map((row: MovementDebugRow) => {
              const isExpanded = expandedId === row._id;
              const isSuperseded = !!row.superseded_by && showSuperseded;
              return (
                <div
                  key={row._id}
                  className={`border border-neutral-200 rounded-lg overflow-hidden${
                    isSuperseded ? " opacity-50" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50"
                    onClick={() => handleRowClick(row._id)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                      <span className="text-[12px] font-mono bg-neutral-100 px-2 py-[2px] rounded shrink-0">
                        {row.category}
                      </span>
                      {row.category === "lab" &&
                        row.source_refs[0] &&
                        data.fileNames[row.source_refs[0]] && (
                          <span className="text-[12px] text-neutral-600 shrink-0 truncate max-w-[180px]">
                            {data.fileNames[row.source_refs[0]]}
                          </span>
                        )}
                      <span className="text-[12px] font-mono text-neutral-500 truncate max-w-[200px]">
                        {row.movement_key}
                      </span>
                      {isSuperseded && (
                        <MBadge variant="grey">
                          {t("debug.movements.superseded")}
                        </MBadge>
                      )}
                      {row.superseded_by && (
                        <span className="text-[12px] text-neutral-500 truncate max-w-[150px]">
                          → {row.superseded_by}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[12px] text-neutral-500 shrink-0 ml-4">
                      {formatEvidenceDate(row.evidence) && (
                        <span>{formatEvidenceDate(row.evidence)}</span>
                      )}
                      {(row.evidence as Record<string, unknown>)?.status !=
                        null && (
                        <span className="bg-neutral-100 px-2 py-[2px] rounded">
                          {String(
                            (row.evidence as Record<string, unknown>).status
                          )}
                        </span>
                      )}
                      {isExpanded ? (
                        <IconChevronUp size={16} />
                      ) : (
                        <IconChevronDown size={16} />
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-neutral-200 p-4">
                      {row.entity && (
                        <div className="flex items-center gap-2 mb-3 text-[12px]">
                          <span className="text-neutral-500 shrink-0">
                            Entity:
                          </span>
                          {row.category === "lab" &&
                          data.parameterNames?.[row.entity] ? (
                            <span className="text-neutral-800 font-medium">
                              {data.parameterNames[row.entity]}
                            </span>
                          ) : (
                            <span className="text-neutral-700 font-mono">
                              {row.entity}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex justify-end mb-2">
                        <button
                          type="button"
                          className="flex items-center gap-1 text-[12px] text-neutral-500 hover:text-neutral-800 transition-colors"
                          onClick={() => handleCopy(row.evidence)}
                        >
                          <IconCopy size={14} />
                          {copied
                            ? t("debug.movements.copied")
                            : t("debug.movements.copyJson")}
                        </button>
                      </div>
                      <pre className="font-mono text-[12px] leading-relaxed bg-neutral-100 p-[12px] rounded overflow-x-auto">
                        {JSON.stringify(row.evidence, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && data && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <button
              type="button"
              className={`text-[14px] px-4 py-2 border border-neutral-200 rounded${
                page <= 1
                  ? " opacity-50 cursor-not-allowed"
                  : " hover:bg-neutral-50"
              }`}
              onClick={() => page > 1 && setPage((p) => p - 1)}
              disabled={page <= 1}
            >
              {t("common.previous")}
            </button>
            <span className="text-[14px] text-neutral-500">
              {t("debug.movements.pagination", { page, total: totalPages })}
            </span>
            <button
              type="button"
              className={`text-[14px] px-4 py-2 border border-neutral-200 rounded${
                page >= totalPages
                  ? " opacity-50 cursor-not-allowed"
                  : " hover:bg-neutral-50"
              }`}
              onClick={() => page < totalPages && setPage((p) => p + 1)}
              disabled={page >= totalPages}
            >
              {t("common.next")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
